import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, MessagesSquare, PlugZap, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { PillTabs } from '../components/alt/PillTabs';

// Order carries the argument: connect the tools, let what they know compound,
// take that intelligence to where people already work, and build on it.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'workspace',
  label: 'Workspace',
  icon: MessagesSquare
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const audiencePhrases = ['companies', 'founders', 'operators', 'teams'];
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  // An IntersectionObserver band was mis-firing here: these sections are much
  // taller than the band, so with direction-dependent tie-breaking the active
  // tab could jump a section early or lag a section behind. A single reference
  // line just below the sticky nav is unambiguous — the active section is
  // simply the last one whose top has passed it.
  useEffect(() => {
    const update = () => {
      const line = (navRef.current?.getBoundingClientRect().bottom ?? 140) + 24;
      let current: SectionId = SECTIONS[0].id;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= line) current = section.id;
      }
      setActiveSection(current);
    };
    update();
    window.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const headerOffset = 76;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 22;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen pt-24 text-[#2B211A]" style={{
    backgroundColor: '#FAF8F4',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
    backgroundSize: '8px 8px'
  }} data-id="element-5978" data-mp="26r12mc3z6pzm">
      <section className="pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-5979" data-mp="2zknuyarno2gs">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5980" data-mp="34ka8ymfb5mqn">
          <div className="mx-auto max-w-5xl text-center" data-id="element-5981" data-mp="34mn09q6yh9gd">
            <h1 className="font-title text-5xl font-bold leading-[0.95] tracking-tight text-[#2B211A] md:text-7xl" data-id="element-5982" data-mp="39koh04gwswoo">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-5983" data-mp="3divw6uvq5pxr">
                <AnimatePresence mode="wait" initial={false} data-id="element-5984" data-mp="2gwsmn6etbgv2">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-5985" data-mp="2lv3x9jcxlvec">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-5986" data-mp="1llckenzshtbc" />
              that want to <HeadlineIcon icon={Zap} data-id="element-5987" data-mp="1lkayfoh8jmeu" />
              <UnderscoredPhrase data-id="element-5988" data-mp="1gmrmwwbx9nql">Go Faster</UnderscoredPhrase>
              <br data-id="element-5989" data-mp="1gm9e7082ph1s" />
              and <HeadlineIcon icon={ShieldCheck} data-id="element-5990" data-mp="1gp40kxandbxu" />
              <UnderscoredPhrase data-id="element-5991" data-mp="1boq3296o9ke2">Stay in Control</UnderscoredPhrase>
            </h1>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-[72px] z-40" data-id="element-5992" data-mp="16vn75uef9yup">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2 px-3 py-4 sm:grid-cols-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-5993" data-mp="11x3kfrs08j1a">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold shadow-sm transition-all sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-md hover:bg-[#C2410C]' : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#EA580C]/30 hover:text-[#2B211A]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-5994" data-mp="nzeyshq457v6">
                <Icon size={19} className={`shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`} data-id="element-5995" data-mp="1202neuho7ak7" />
                <span className="min-w-0 leading-tight" data-id="element-5996" data-mp="nb69igsxn3bo">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-5997" data-mp="s97p2rr5gkie">
        <section id="connected-apps" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5998" data-mp="s8y6hp8fpfmq">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5999" data-mp="1fxqw73ygqr8w">
            <ConnectedAppsPillar data-id="element-6000" data-mp="1krlmiq8kq4gy" />
            <div className="mt-20 md:mt-28" data-id="element-6001" data-mp="1ksn31a8czj62">
              <ConnectedAppsTable data-id="element-6002" data-mp="35g7cgs8zyw02" />
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-40 border-b border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-6003" data-mp="30i5w4f2yhasa">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6004" data-mp="3f721u5hjibpq">
            <BrainFlowExperiment data-id="element-6005" data-mp="3a7pbvbvdgoix" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-6006" data-mp="3a8h88owsmqa9">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6007" data-mp="2mjy14cphcjjr">
            <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-6008" data-mp="2hkl3cwx2ls2e">
              <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-6009" data-mp="2vj1jfegtoux7">
                AI Co Worker
              </p>
              <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-6010" data-mp="2rjap50s37wnx">
                No new tab for
                <br data-id="element-6011" data-mp="6rqprak0ykro" />
                your team to open.
              </h2>
              <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-6012" data-mp="209y13er4brt6">
                Everything your company builds in Giga can show up as a co
                worker in Slack, ChatGPT, and Claude Code — with Teams and
                iMessage next. Same connections, same brain, no new tab.
              </p>
            </div>

            {/* One centred column: the surface switcher sits directly above the
             frame it controls, so the relationship needs no explaining. */}
            <div className="mx-auto max-w-[820px]" data-id="element-6013" data-mp="2nyqwna3tvxn9">
              {/* Deliberately quieter and smaller than the four pillar tabs at
               the top of the page — this is a local control, not navigation —
               but the platform logos stay so each one is identifiable. */}
              <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden justify-center md:mb-10 md:flex" data-id="element-6014" data-mp="yklus4ik61t8" />
              <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-6015" data-mp="13jp07q2gaa1q" />
            </div>
          </div>
        </section>

        <section id="workspace" className="scroll-mt-40 bg-white py-20 md:py-28" data-id="element-6016" data-mp="3c2gh05w5ikf7">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6017" data-mp="381ejbthvugfc">
            <PlatformPillar data-id="element-6018" data-mp="381mthdmmf7mu" />
          </div>
        </section>

        {/* White canvas: the diagram's group panels are cream, so they need a
         lighter ground now that the enclosing card is gone. */}
        <section className="border-t border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-6019" data-mp="3l2h8xkyhcfl">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6020" data-mp="3vrqx3kzt77du">
            <ArchitectureWidget data-id="element-6021" data-mp="3qr35ax3ramik" />
          </div>
        </section>

        <section className="border-t border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-6022" data-mp="3lsrxst0xoo3i">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6023" data-mp="3lv5oyqshtg5s">
            <AccelerationControlSection data-id="element-6024" data-mp="2feahtyi00k8b" />
            <ClosingCTA data-id="element-6025" data-mp="2admj0qnj2z3j" />
          </div>
        </section>
      </main>
    </div>;
}
// Sized in em so the tile tracks the headline as it scales across breakpoints.
function HeadlineIcon({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  // Bare mark, no tile: at headline scale the filled square read as a button
  // and boxed the icon away from the words it belongs to.
  return <span aria-hidden="true" className="mr-[0.14em] inline-flex size-[0.78em] -translate-y-[0.04em] items-center justify-center align-middle" data-id="element-6026" data-mp="2aml1kruabj5j">
      <Icon className="size-full text-[#EA580C]" strokeWidth={2.4} data-id="element-6027" data-mp="2jkdhny7tyutz" />
    </span>;
}
function UnderscoredPhrase({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="relative inline-block align-baseline" data-id="element-6028" data-mp="2eog3nyk1i0kd">
      <span className="absolute inset-x-0 bottom-[-4px] h-[9px] bg-[#EA580C]" aria-hidden="true" data-id="element-6029" data-mp="2tiuaz1u8rlj7" />
      <span className="relative z-10" data-id="element-6030" data-mp="2okssoj4n3q8s">{children}</span>
    </span>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-6031" data-mp="u8l0hdjpdngp" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6032" data-mp="2w2r08i39gr9d">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-6033" data-mp="3110vstswdtvi" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-6034" data-mp="2r4phxzdnsvyy">
          <MoreThanAnswers embedded data-id="element-6035" data-mp="2d67ve4zhppim" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6036" data-mp="2i4j6ej1iu62f">
        {mobileTabs}
        <CodeTerminal data-id="element-6037" data-mp="gce7uopioaiq" />
      </div>;
  }
  // Width matches SlackPanel's own max-width so the orange halo hugs the frame
  // instead of showing a strip of background down each side.
  return <div className="relative mx-auto w-full max-w-[720px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6038" data-mp="et4ioa19sirc">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-6039" data-mp="4wrolb1tc2w2" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-6040" data-mp="3w4w1d0wame29">
        <SlackPanel className="w-full" heightClass="h-[520px] sm:h-[600px]" data-id="element-6041" data-mp="xkxbo4gca3z2" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-6042" data-mp="1bl80l1y398hl" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-6043" data-mp="1nvzcvos6hhu9" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-6044" data-mp="1su0xie595p7u" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-6045" data-mp="1xvq4nipctiqn">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-6046" data-mp="26tac59dt8b9p">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  // Same control as the architecture diagram's preset tabs, with each
  // platform's logo added so the surfaces are identifiable at a glance.
  return <div className={`w-full items-center ${className}`} data-id="element-6047" data-mp="umyxfhrua8d">
      <PillTabs ariaLabel="Chat surface" value={activeSurface} onChange={id => setActiveSurface(id as SurfaceKey)} className={tabClassName} items={[{
      id: 'slack',
      label: 'Slack',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
    }, {
      id: 'code',
      label: 'Claude',
      ariaLabel: 'Claude',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/claude-ai.svg'
    }, {
      id: 'chatgpt',
      label: 'ChatGPT',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/chatgpt.svg'
    }]} data-id="element-6048" data-mp="tbmkra9fu5n" />
    </div>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-6049" data-mp="27gxya2pjw339">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-6050" data-mp="27elf5m823mom" data-mpi={groupIndex}>
          <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-6051" data-mp="23h7b08z8g53h">
            {group.heading}
          </h2>
          <div className="mt-8 border-y border-neutral-200" data-id="element-6052" data-mp="1thgg466rfo2v">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-6053" data-mp="1opom1loeb07z" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-6054" data-mp="3hfh3v8fkuti8">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-6055" data-mp="3mch9xjcffw9c">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  return <div className="mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center md:mt-28 md:px-16 md:py-20" data-id="element-6056" data-mp="3vdnuq4m3zt8e">
      <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-[52px]" data-id="element-6057" data-mp="2ymdc5slx6qq9">
        One platform to run your company's AI on.
      </h2>
      <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-6058" data-mp="37n1nugnucezz">
        Connect your tools, build your workflows, put a co worker where your
        team already is, and let the brain get sharper every week.
      </p>
      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-6059" data-mp="2jyify28pe48f">
        <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-6060" data-mp="1dbg15kdm74mb">
          Get started for free
          <ArrowRight size={17} data-id="element-6061" data-mp="1nafcjaprled0" />
        </Link>
        <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-colors hover:border-white/60" data-id="element-6062" data-mp="1n82l86y49rna">
          Book a demo
        </Link>
      </div>
      <p className="mt-6 text-[13.5px] text-white/45" data-id="element-6063" data-mp="1rc0isd7smue8">
        $100 in credits included · no card needed
      </p>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-48px_rgba(43,33,26,0.4)]" data-id="element-6064" data-mp="r2xr0q7kk8fh">
      <div className="overflow-x-auto" data-id="element-6065" data-mp="m2a004jcbrl9">
        <table className="w-full min-w-[660px] border-collapse text-left" data-id="element-6066" data-mp="zwan210xjq5s">
          <thead data-id="element-6067" data-mp="vu5wxc9oxbnw">
            <tr className="border-b border-neutral-200" data-id="element-6068" data-mp="vtnt9ukp7v5y">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-6069" data-mp="vwac4qd1hx3h">
                <span className="sr-only" data-id="element-6070" data-mp="85eaz2mp1pjk">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6071" data-mp="361o4hvtoigz">
                ChatGPT
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6072" data-mp="h2x1unn0qy3d">
                Claude
              </th>
              <th scope="col" className="w-[18%] bg-[#FFF8F3] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6073" data-mp="1sj5a95p4t3my">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-6074" data-mp="l7fiptc13wlf">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="border-b border-neutral-100 last:border-b-0" data-id="element-6075" data-mp="q6s9uq9xnptt">
                <th scope="row" className="px-5 py-3.5 text-[14px] font-semibold leading-snug text-[#2B211A] md:px-6" data-id="element-6076" data-mp="u8vlbewlquf3">
                  <span className="block" data-id="element-6077" data-mp="z7ysb4w57app">{row.feature}</span>
                  <span className="mt-1 block max-w-xl text-[12.5px] font-normal leading-[1.45] text-[#8A7B70]" data-id="element-6078" data-mp="z5cggt27y91o">
                    {row.description}
                  </span>
                </th>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6079" data-mp="6goroni9h1jd">
                  <ComparisonValue value={row.chatgpt} data-id="element-6080" data-mp="bhcjvci87of6" />
                </td>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6081" data-mp="g949nktk5q49">
                  <ComparisonValue value={row.anthropic} data-id="element-6082" data-mp="gap8hmmwnv7x" />
                </td>
                <td className="bg-[#FFF8F3] px-4 py-3.5 text-[#2B211A]" data-id="element-6083" data-mp="3eykzdpphec6q">
                  <ComparisonValue value={row.giga} highlight data-id="element-6084" data-mp="3jwmrf04vxluc" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-6085" data-mp="32extnboqkrzh">
        <Check size={19} strokeWidth={2.6} aria-hidden="true" data-id="element-6086" data-mp="2ygq8m4n8p4ih" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-neutral-300" aria-label="Not supported" data-id="element-6087" data-mp="2thdklgkmtreb">
        <X size={19} strokeWidth={2.4} aria-hidden="true" data-id="element-6088" data-mp="2on0zl23ic80g" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-neutral-300" data-id="element-6089" data-mp="2eqoa6gezwgbi">—</span>;
  }
  return <span className={`text-[12px] font-semibold leading-tight ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-6090" data-mp="2eolbz93ovgvp">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6091" data-mp="9mbmeo8xj9a1">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6092" data-mp="9nd6tjbu584f">
        <Icon size={22} strokeWidth={2.4} data-id="element-6093" data-mp="dnx5wsfyylt7" />
      </span>
      {label}
    </p>;
}